/**
 * Auth context — provides user state from /auth/me endpoint.
 * Identity managed by centralized SSO (sso.wilreji.com).
 *
 * Local dev modes:
 *   VITE_MOCK_AUTH=true → skip SSO entirely, inject mock user (fastest)
 *   Default (no flag)   → use staging SSO with localhost redirect (real auth)
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ROUTES, type SessionUser } from '@worktracker/shared';
import { apiGet, apiPost } from '@/lib/api';
import { getSsoLogoutUrl } from '@/lib/sso';

const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';

if (MOCK_AUTH && import.meta.env.PROD) {
  throw new Error('FATAL: VITE_MOCK_AUTH must not be enabled in production builds');
}

const MOCK_USER: SessionUser = {
  ssoUserId: 'mock-dev-user',
  name: 'Dev User',
  email: 'dev@willdesign.dev',
  githubUsername: 'dev-user',
  avatar: null,
};

interface AuthContextValue {
  user: SessionUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      if (MOCK_AUTH) {
        return MOCK_USER;
      }
      const result = await apiGet<SessionUser>(API_ROUTES.AUTH_ME);
      if (!result.success) {
        return null;
      }
      return result.data ?? null;
    },
    staleTime: Infinity,
    retry: false,
  });

  const user = data ?? null;

  const logout = async () => {
    if (MOCK_AUTH) {
      queryClient.setQueryData(['auth', 'me'], null);
      globalThis.location.href = '/login';
      return;
    }
    await apiPost(API_ROUTES.AUTH_LOGOUT);
    queryClient.setQueryData(['auth', 'me'], null);
    globalThis.location.href = getSsoLogoutUrl();
  };

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, isAuthenticated: !!user, logout }),
    [user, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
